/**
 * The Legal Agent portal keeps its state in the URL query. RouterContext's `setQuery`
 * replaces the whole query string, so every writer serializes the full parsed query
 * with one field changed: `setQuery(serializeLegalAgentQuery({ ...query, actor }))`.
 */
export type LegalAgentActor = 'agent' | 'user';

export type LegalAgentScopeKind = 'projects' | 'releaseGroups';

export type LegalAgentQuery = {
  actor: LegalAgentActor | null;
  //  Projects and release groups are mutually exclusive; this says which kind is active.
  scope: LegalAgentScopeKind;
  projects: string[];
  releaseGroups: string[];
};

const ACTORS: LegalAgentActor[] = ['agent', 'user'];

//  qs gives a string for one value, an array for up to 20, and a plain object keyed by index above its
//  default arrayLimit of 20 (the router parses the query with qs defaults). Anything else is junk from a
//  hand-edited URL.
function parseStringList(raw: unknown): string[] {
  let values: unknown[];
  if (Array.isArray(raw)) {
    values = raw;
  } else if (raw !== null && typeof raw === 'object') {
    values = Object.values(raw);
  } else {
    values = [raw];
  }
  return [...new Set(values.filter((value): value is string => typeof value === 'string' && value !== ''))];
}

export function parseLegalAgentQuery(val: Record<string, unknown>): LegalAgentQuery {
  const scope: LegalAgentScopeKind = val.scope === 'releaseGroups' ? 'releaseGroups' : 'projects';
  return {
    actor: ACTORS.find((actor) => actor === val.actor) ?? null,
    scope,
    projects: scope === 'projects' ? parseStringList(val.projects) : [],
    releaseGroups: scope === 'releaseGroups' ? parseStringList(val.releaseGroups) : [],
  };
}

export function serializeLegalAgentQuery(query: LegalAgentQuery): Record<string, unknown> {
  return {
    ...(query.actor ? { actor: query.actor } : {}),
    ...(query.scope === 'releaseGroups' ? { scope: 'releaseGroups' } : {}),
    ...(query.scope === 'projects' && query.projects.length > 0 ? { projects: query.projects } : {}),
    ...(query.scope === 'releaseGroups' && query.releaseGroups.length > 0 ? { releaseGroups: query.releaseGroups } : {}),
  };
}

export function withScopeKind(query: LegalAgentQuery, scope: LegalAgentScopeKind): LegalAgentQuery {
  return { ...query, scope, projects: [], releaseGroups: [] };
}

export function isLegalAgentQueryFiltered(query: LegalAgentQuery): boolean {
  return query.actor !== null || query.projects.length > 0 || query.releaseGroups.length > 0;
}
